// frontend/src/pages/PaychequeList/components/ChequeModal.tsx
// Full cheque preview with print + save-as-PDF. Rendered only when `target` is set.
// Reusable: any page that shows a cheque (PaychequeDetail, etc.) can import this.

import React from "react";
import type { Paycheque, CompanyInfo } from "../types";
import {
  fmtDate,
  fmtMoney,
  numberToWords,
  API_URL,
} from "../helpers";
import { C, FONT } from "../styles";

interface ChequeModalProps {
  target: Paycheque;
  company: CompanyInfo;
  onClose: () => void;
}

export default function ChequeModal({
  target,
  company,
  onClose,
}: ChequeModalProps): React.ReactElement {
  function handlePrint(): void {
    document.body.classList.add("printing-cheque");
    setTimeout(() => {
      window.print();
      document.body.classList.remove("printing-cheque");
    }, 50);
  }

  function handleSavePdf(): void {
    const token =
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      "";
    const url =
      API_URL +
      "/api/v1/payroll/paycheques/" +
      target.id +
      "/cheque-pdf?token=" +
      encodeURIComponent(token);
    window.open(url, "_blank");
  }

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(18,38,43,0.5)",
          zIndex: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <div
          className="cheque-print-target"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: C.chequePaper,
            border: "1px solid " + C.chequeBorder,
            borderRadius: 4,
            boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
            width: 720,
            maxWidth: "94vw",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <div style={{ padding: "32px 40px", fontFamily: "Georgia, serif", color: C.ink }}>
            {/* Header: company + cheque number */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                  {company.name || "Company"}
                </div>
                <div style={{ fontSize: 12, whiteSpace: "pre-line" }}>{company.address}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6, fontFamily: FONT }}>
                  Cheque no
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, fontVariantNumeric: "tabular-nums lining-nums" }}>
                  {target.cheque_number || ""}
                </div>
              </div>
            </div>

            {/* Payee + date */}
            <div style={{ display: "flex", justifyContent: "space-between", gap: 32, marginBottom: 32 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6, fontFamily: FONT }}>
                  Pay to the order of
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, borderBottom: "1px solid " + C.ink, paddingBottom: 6 }}>
                  {target.employee_name}
                </div>
              </div>
              <div style={{ width: 220 }}>
                <div style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6, fontFamily: FONT }}>
                  Date
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, borderBottom: "1px solid " + C.ink, paddingBottom: 6, fontVariantNumeric: "tabular-nums lining-nums" }}>
                  {fmtDate(target.pay_date)}
                </div>
              </div>
            </div>

            {/* Amount in words + numeric amount */}
            <div style={{ display: "flex", alignItems: "flex-end", gap: 16, marginBottom: 40 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontStyle: "italic", borderBottom: "1px solid " + C.ink, paddingBottom: 6 }}>
                  {numberToWords(target.net_pay)}
                </div>
              </div>
              <div style={{ minWidth: 180, border: "2px solid " + C.ink, padding: "10px 16px", textAlign: "right" }}>
                <span style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums lining-nums" }}>
                  {fmtMoney(target.net_pay)}
                </span>
              </div>
            </div>

            {/* Memo + signature */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24 }}>
              <div style={{ flex: 1, marginRight: 40 }}>
                <div style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6, fontFamily: FONT }}>
                  Memo
                </div>
                <div style={{ fontSize: 13, fontStyle: "italic", borderBottom: "1px solid " + C.ink, paddingBottom: 6 }}>
                  Payroll paycheque
                </div>
              </div>
              <div style={{ width: 260 }}>
                <div style={{ height: 24, borderBottom: "1px solid " + C.ink }} />
                <div style={{ fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 6, textAlign: "center", fontFamily: FONT }}>
                  Authorized signature
                </div>
              </div>
            </div>
          </div>

          {/* Footer actions (hidden on print) */}
          <div
            className="no-print-in-cheque"
            style={{
              padding: "16px 40px 24px",
              borderTop: "1px solid " + C.line,
              background: "#FFFFFF",
              display: "flex",
              gap: 10,
              justifyContent: "center",
              fontFamily: FONT,
            }}
          >
            <button
              onClick={handlePrint}
              style={{ padding: "12px 24px", background: C.card, border: "1.5px solid " + C.ink, borderRadius: 10, color: C.ink, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
            >
              Print cheque
            </button>
            <button
              onClick={handleSavePdf}
              style={{ padding: "12px 24px", background: C.brand, border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
            >
              Save as PDF
            </button>
            <button
              onClick={onClose}
              style={{ padding: "12px 24px", background: C.card, border: "1.5px solid " + C.ink, borderRadius: 10, color: C.ink, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Print CSS: hide everything except the cheque-print-target during print */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          body.printing-cheque * { visibility: hidden !important; }
          body.printing-cheque .cheque-print-target,
          body.printing-cheque .cheque-print-target * { visibility: visible !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
          body.printing-cheque .no-print-in-cheque,
          body.printing-cheque .no-print-in-cheque * { visibility: hidden !important; display: none !important; }
          body.printing-cheque .cheque-print-target { position: absolute !important; left: 0 !important; top: 0 !important; right: 0 !important; margin: 0 auto !important; width: 100% !important; max-width: 7.5in !important; max-height: none !important; height: auto !important; overflow: visible !important; background: white !important; box-shadow: none !important; border: none !important; padding: 0 !important; }
        }
      `}</style>
    </>
  );
}